import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';
import useProjectStore from '../stores/projectStore';
import { useSeoMeasures } from '../hooks/useSeoChecklist';

function impactStyle(impact) {
  const c = impact === 'hoch' ? '#dc2626' : impact === 'mittel' ? '#b8860b' : '#6b7280';
  return { color: c, backgroundColor: c + '18' };
}

const DRAFTABLE = ['content', 'copy', 'dev', 'technical'];

export default function Massnahmen() {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const projects = useProjectStore((s) => s.projects);
  const current = projects.find((p) => p.id === currentProjectId);
  const qc = useQueryClient();

  const { data, isLoading } = useSeoMeasures(currentProjectId);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [applying, setApplying] = useState(null);
  const [applyMsg, setApplyMsg] = useState({});
  const [pagePick, setPagePick] = useState(null);
  const groups = data?.groups || [];

  const makeDraft = async (id) => {
    setBusy(id);
    try {
      const { data: res } = await api.post(`/agent-tasks/${id}/draft`, {}, { skipProject: true });
      setDrafts((d) => ({ ...d, [id]: res.ok ? res.draft : '⚠ ' + (res.error || 'Fehler') }));
    } catch {
      setDrafts((d) => ({ ...d, [id]: '⚠ Entwurf fehlgeschlagen' }));
    } finally {
      setBusy(null);
    }
  };

  const apply = async (id, pageId = null) => {
    setApplying(id);
    setApplyMsg((m) => ({ ...m, [id]: null }));
    try {
      const { data: res } = await api.post(
        `/agent-tasks/${id}/apply`,
        pageId ? { page_id: pageId } : {},
        { skipProject: true }
      );
      if (res.ok) {
        const label = res.field === 'meta_title' ? 'Title' : 'Meta-Description';
        setApplyMsg((m) => ({ ...m, [id]: { ok: true, text: `Im CMS umgesetzt ✓ (${label})` } }));
        setPagePick(null);
        qc.invalidateQueries({ queryKey: ['seo-measures', currentProjectId] });
        qc.invalidateQueries({ queryKey: ['seo-checklist', currentProjectId] });
      } else {
        setApplyMsg((m) => ({ ...m, [id]: { ok: false, text: res.error } }));
      }
    } catch (e) {
      const d = e.response?.data;
      if (d?.needs_page) {
        setPagePick({ taskId: id, pages: d.pages || [], selected: d.pages?.[0]?.id ?? '' });
      } else {
        setApplyMsg((m) => ({ ...m, [id]: { ok: false, text: d?.error || 'Umsetzen fehlgeschlagen' } }));
      }
    } finally {
      setApplying(null);
    }
  };

  const copy = (txt) => navigator.clipboard?.writeText(txt);

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50]">SEO-Maßnahmen</h1>
        <p className="text-[13.5px] text-gray-500 mt-1">
          {current?.name ? <>Offene Maßnahmen für <span className="font-medium text-gray-700">{current.name}</span> — nach zuständigem Agenten ({data?.total ?? 0} offen). Title/Meta-Maßnahmen kann der Agent als <strong>Entwurf</strong> liefern und bei verbundenem CMS direkt <strong>umsetzen</strong>.</> : 'Kein Projekt ausgewählt.'}
        </p>
      </div>

      {isLoading ? (
        <div className="text-[13px] text-gray-500 p-6">Lade…</div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-black/[.07] p-8 text-center">
          <div className="text-[14px] text-gray-700 font-medium mb-1">Keine offenen Maßnahmen</div>
          <div className="text-[12.5px] text-gray-500">Starte die Prüfung in der SEO-Checkliste, dann erscheinen hier die Maßnahmen.</div>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.assignee}>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-[13px] font-semibold text-[#0D3B50]">{g.label}</h2>
                <span className="text-[11.5px] text-gray-400">{g.items.length}</span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
              <ul className="space-y-2">
                {g.items.map((t) => {
                  const draft = drafts[t.id] ?? t.draft;
                  const canDraft = DRAFTABLE.includes(t.type) && t.target_url;
                  const isDev = ['dev', 'technical'].includes(t.type);
                  const canApply = draft && !isDev && t.target_url;
                  const msg = applyMsg[t.id];
                  return (
                    <li key={t.id} className="bg-white rounded-xl border border-black/[.07] p-3.5">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded shrink-0 uppercase" style={impactStyle(t.impact)}>{t.impact}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] text-gray-800">{t.instruction || t.title}</div>
                          <div className="text-[11px] text-gray-400 mt-0.5 flex flex-wrap gap-x-3">
                            {t.section && <span>{t.section}</span>}
                            {t.priority != null && <span>Prio {t.priority}</span>}
                            {t.tools && <span className="truncate">🛠 {t.tools}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {canDraft && (
                            <button onClick={() => makeDraft(t.id)} disabled={busy === t.id} className="text-[11.5px] px-2.5 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                              {busy === t.id ? 'Erstelle…' : (draft ? 'Neu' : (isDev ? 'Lösung erstellen' : 'Entwurf erstellen'))}
                            </button>
                          )}
                          {canApply && (
                            <button onClick={() => apply(t.id)} disabled={applying === t.id} className="text-[11.5px] px-2.5 py-1 rounded-lg bg-[#0D3B50] text-white hover:opacity-90 disabled:opacity-50">
                              {applying === t.id ? 'Setze um…' : 'Umsetzen'}
                            </button>
                          )}
                        </div>
                      </div>
                      {draft && (
                        <div className="mt-2.5 ml-8 rounded-lg bg-[#0D3B50]/[.04] border border-[#0D3B50]/15 p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] uppercase tracking-wider font-semibold text-[#0D3B50]">{isDev ? 'Lösung / Code' : 'Entwurf'}</span>
                            <button onClick={() => copy(draft)} className="text-[11px] text-[#0D3B50] hover:underline">Kopieren</button>
                          </div>
                          <div className={`text-gray-700 whitespace-pre-wrap ${isDev ? 'font-mono text-[11px]' : 'text-[12.5px]'}`}>{draft}</div>
                        </div>
                      )}
                      {pagePick?.taskId === t.id && (
                        <div className="mt-2 ml-8 flex flex-wrap items-center gap-2">
                          <span className="text-[11.5px] text-gray-500">Keine eindeutige Seite — wähle:</span>
                          <select
                            value={pagePick.selected}
                            onChange={(e) => setPagePick((p) => ({ ...p, selected: e.target.value }))}
                            className="border border-gray-200 rounded-lg px-2 py-1 text-[12px] outline-none focus:border-[#0D3B50]"
                          >
                            {pagePick.pages.map((p) => (
                              <option key={p.id} value={p.id}>{p.title} (/{p.slug})</option>
                            ))}
                          </select>
                          <button onClick={() => apply(t.id, pagePick.selected)} disabled={applying === t.id} className="text-[11.5px] px-2.5 py-1 rounded-lg bg-[#0D3B50] text-white hover:opacity-90 disabled:opacity-50">
                            Auf dieser Seite umsetzen
                          </button>
                        </div>
                      )}
                      {msg && (
                        <div className={`mt-2 ml-8 text-[11.5px] ${msg.ok ? 'text-[#0C9488]' : 'text-red-600'}`}>
                          {msg.ok ? '✓ ' : '⚠ '}{msg.text}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
